
import EoList from '../EoList/EoList.vue'
import EoTab from './EoTab'

export default {
  name: 'EoTabs',
  components: {
    EoList,
    EoTab,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /**
     * Index of selected EoTab
     */
    value: {
      type: [String, Number],
      default: 0,
    },
    /**
     * Style: width of each tab: (1-12 OR pixels)
     */
    tabWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 1 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Style: overflow horizontally: (fullwidth, 1, 2, 3, 4, 5)
     */
    overflowX: {
      type: [String, Number],
      default: null,
      validator (amount) {
        return amount == 'fullwidth' || (1 <= amount && amount <= 5)
      },
    },
    /**
     * Style: background color of the tab labels bar
     */
    color: {
      type: String,
      default: '',
    },
    /**
     * Style: text color of the tab labels bar
     */
    labelTextColor: {
      type: String,
      default: 'primary',
    },
    /**
     * Style: text color of the selected tab
     */
    labelTextColorActive: {
      type: String,
      default: 'primary',
    },
    /**
     * Style: font of the labels bar: (body) (heading)
     */
    font: {
      type: String,
      default: 'body',
      validator: prop => Object.values(['body', 'heading']).includes(prop) || prop === undefined || prop === null,
    },
    /**
     * Style: color of the tabs slider
     */
    sliderColor: {
      type: String,
      default: '',
    },
    /**
     * Style: make tabs slider full height
     */
    sliderCover: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: color of the scroll buttons
     */
    scrollButtonsColor: {
      type: String,
      default: 'white',
    },
    /**
     * Style: icon color of the scroll buttons
     */
    scrollButtonsTextColor: {
      type: String,
      default: 'primary',
    },
    /**
     * Style: place the scroll buttons inside the group boundaries
     */
    scrollButtonsInline: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      isMounted: false,
      selectedIndex: null,
      tabs: [],
      list: null,
      slider: {
        left: 0,
        width: 0,
      },
      dynamicSliderColor: null,
      hasChangedByRoute: false,
    }
  },
  computed: {
    classes () {
      return {
        'eo-tabs': true,
        'has-selected-tab': this.hasSelectedTab,
        'has-content': this.hasContent,
        'is-slider-cover': this.sliderCover,
        ['is-font-' + this.font]: !!this.font,
        ['color--' + this.color]: !!this.color,
      }
    },
    classesList () {
      return {
        'eo-tabs__list': true,
        ['color--' + this.color]: !!this.color,
      }
    },
    classesSlider () {
      return {
        'eo-tabs__slider': true,
        ['color--' + this.dynamicSliderColor]: !!this.dynamicSliderColor,
      }
    },
    stylesSlider () {
      return {
        transform: `translateX(${this.slider.left}px)`,
        width: `${this.slider.width}px`,
      }
    },
    hasSelectedTab () {
      return this.value !== null && this.value !== undefined
    },
    hasContent () {
      if (this.tabs.length) {
        // Check already initiated tabs (quicker)
        return this.tabs.some((tab) => {
          return tab.hasContent
        })
      } else {
        if (!this.$slots.default) return false
        // Check unitiated tabs (slower, but needed at onCreate)
        return this.$slots.default.some(child => {
          if (child.text !== undefined || child.isComment) return false
          if (!child.componentOptions) return
          if (child.componentOptions.tag !== 'eo-tab') return false
          if (!child.componentOptions.children) return false
          return true
        })
      }
    },
  },
  watch: {
    value: 'setSelectedTab',
    tabs: 'positionSlider',
    tabWidth: 'positionSlider',
    hasContent: 'positionSlider',
    selectedIndex: 'positionSlider',
    'list.horizontalOverflow': 'positionSlider',
  },
  methods: {
    setSelectedTab (newIndex) {
      this.selectedIndex = newIndex

      // Change the selected state of each tab
      Object.entries(this.tabs).forEach(([i, tab]) => {
        tab.isSelected = (i == newIndex)
      })

      // Make sure the selected tab is in viewport
      this.scrollToSelectedTab()

      // Update the slider color
      if (newIndex !== null && newIndex !== undefined) {
        this.dynamicSliderColor = this.tabs[newIndex]?.sliderColor || this.sliderColor || this.tabs[newIndex]?.labelTextColor
      }

      // Let the parent know the selected tab has changes
      if (newIndex !== this.value) {
        /**
         * Emitted when a tab is selected
         * @event input
         */
        this.$emit('input', newIndex)
      }
    },
    async scrollToSelectedTab () {
      if (this.$refs.list && (this.value !== null || this.value !== undefined)) {
        if (this.$refs.list.hasOverflow) {
          this.$refs.list.scrollCarouselToIndex(this.value)
        }
      }
    },
    async positionSlider () {
      // Position the slider underneath the selected tab
      if (!this.tabs.length) return
      if (!this.$refs.list) return
      if (this.selectedIndex == undefined || this.selectedIndex == null) return
      let left = 0
      let width = 0
      
      const selectedTab = this.tabs[this.selectedIndex]
      if (selectedTab) {
        const tabElement = selectedTab.$el || selectedTab.elm
        const listElement = this.$refs.list.$el || this.$refs.list.elm
        if (tabElement && listElement) {
          left = tabElement.offsetLeft
          width = tabElement.offsetWidth
        }
      }

      this.slider.left = left
      this.slider.width = width
    },
    async onResize () {
      this.positionSlider()
    },
    async findTabMatchingRoute () {
      let tabToActivate = this.tabs.find((tab) => {
        return tab.exact && tab.hasRouteMatchExact
      })
      if (!tabToActivate) {
        tabToActivate = this.tabs.find((tab) => {
          return !tab.exact && tab.hasRouteMatch
        })
      }
      if (tabToActivate) {
        this.setSelectedTab(tabToActivate.index)
        this.hasChangedByRoute = true
      } else if (this.hasChangedByRoute) {
        this.setSelectedTab(null)
        this.hasChangedByRoute = false
      } else {
        this.setSelectedTab(this.value)
      }
    },
  },
  render: function (createElement) {
    const elementSlider = createElement(
      'div',
      {
        class: this.classesSlider,
        style: this.stylesSlider,
        attrs: {
          'eo-list-exclude': true,
        },
      },
    )

    const elementTabs = createElement(
      'eo-list', {
        ref: 'list',
        class: this.classesList,
        props: {
          buttonsColor: this.scrollButtonsColor || this.color,
          buttonsInline: this.scrollButtonsInline || this.hasContent,
          buttonsSize: 'xs',
          buttonsTextColor: this.scrollButtonsTextColor,
          carousel: true,
          gap: (!!this.tabWidth || this.hasContent) ? 0 : 4,
          itemSize: this.tabWidth,
          overflowX: this.overflowX,
        },
        attrs: {
          tabindex: this.$attrs.tabindex,
        },
        directives: [{
          name: 'resize',
          value: { callback: this.onResize },
        }],
      }, [
        // @slot Place one or more EoTab components here
        this.$slots.default,
        elementSlider,
      ],
    )

    const elementTabsContent = createElement(
      'div', {
        class: 'eo-tabs__content',
        directives: [{
          name: 'show',
          value: this.hasContent,
        }],
      },
      this.tabs.map((tab) => {
        return createElement(
          'div',
          {
            class: tab.classesContent,
            directives: [{
              name: 'show',
              value: tab.isSelected,
            }],
            key: tab.index,
          },
          tab.$slots?.default,
        )
      }),
    )

    return createElement(
      'div', {
        class: this.classes,
      },
      [
        elementTabs,
        elementTabsContent,
      ],
    )
  },
}
